import inject from 'seacreature/lib/inject'
import { useContext, useEffect, useState } from 'react'
import page from 'page'
import {
  Fab,
  CircularProgress,
  Breadcrumbs,
  Button,
  Box,
  InputAdornment,
  Link,
  ListSubheader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormGroup,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox
} from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { NavigateNext, ContactPage } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

inject('pod', ({ HubContext, StateContext, IndexedDB }) => {
  inject('route', [
    '/contacts/:contact_id/canvasses/:canvass_id/view',
    p => () => {
      const hub = useContext(HubContext)
      const { contact_id, canvass_id } = p.params
      const { data } = useContext(StateContext)
      const [state, set_state] = useState({})

      useEffect(() => {
        const contact = data?.contacts?.find(c => c.id == contact_id)
        if (data?.contacts && !contact) return page('/contacts')
        if (contact) {
          contact.name = [contact.first_name, contact.last_name].join(' ').trim()
          const canvass = contact.canvasses.find(c => c.id == canvass_id)
          const shed = contact.sheds.find(s => s.shed_id == canvass.shed_id)
          set_state(() => ({
            contact,
            canvass: {
              id: canvass.id,
              contact_id: canvass.contact_id,
              // sheds: [],
              date: canvass.date,
              island: shed.island,
              shed_id: canvass.shed_id,
              dairy_number: shed.dairy_number,
              note_farm: canvass.note_farm,
              calving_date: canvass.calving_date,
              ttx_volume_on_hand: canvass.ttx_volume_on_hand,
              days_remaining: canvass.days_remaining,
              agreement_status: canvass.agreement_status,
              reason_no_business: canvass.reason_no_business,
              note_no_business: canvass.canvas_note_no_business,
              farm_details: {
                region: shed.region,
                address: shed.address,
                herd_size: shed.herd_size,
                contacts: [
                  {
                    id: contact.id,
                    contact_id: contact.id,
                    role: shed.role,
                    name: [contact.first_name, contact.last_name].join(' ').trim(),
                    trading_name: contact.trading_name,
                    email_address: contact.email_address,
                    phone_number: contact.phone_number,
                    retailer_id: canvass.retailer_id
                  }
                ],
                products: canvass.products.map(p => ({ _id: uuidv4(), ...p }))
              },
              created_at: canvass.created_at,
              available_recipients: canvass.recipients,
              recipients: []
              // regions: places,
              // display_reason_no_business_fields: false
            },
            shed
          }))
        }
      }, [data])

      const agreement_status_options =
        state.canvass?.agreement_status !== 'No Business' ? ['Pending', 'Confirmed'] : ['No Business']

      const change_agreement_status = e => {
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            agreement_status: e.target.value
          }
        }))
      }

      const add_product = e => {
        const product_id = e.target.value
        const product = data.products.find(p => p.id == product_id)
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            farm_details: {
              ...s.canvass.farm_details,
              products: [
                ...(s.canvass.farm_details.products ?? []),
                {
                  _id: uuidv4(),
                  product_id,
                  name: product.name,
                  category: product.category,
                  quantity: 1,
                  price: product.price,
                  pack_size: product.pack_size,
                  pack_size_unit: product.pack_size_unit
                }
              ]
            }
          }
        }))
      }

      const update_product = (_id, field) => e => {
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            farm_details: {
              ...s.canvass.farm_details,
              products: s.canvass.farm_details.products.map(p => {
                if (p._id != _id) return p
                if (field.includes('date')) {
                  return { ...p, [field]: e?.toFormat('dd-MM-yyyy') }
                }
                if (field == 'quantity') {
                  const product = data.products.find(pp => pp.id == p.product_id)
                  return {
                    ...p,
                    [field]: Number(e.target.value) ? e.target.value : '',
                    price: Number(e.target.value)
                      ? (product.price * e.target.value)
                          .toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'NZD'
                          })
                          .replace('NZ$', '')
                      : ''
                  }
                }
                if (field == 'retailer_id') {
                  const retailer = data.retailers.find(r => r.id == e.target.value)
                  console.log(retailer)
                  return {
                    ...p,
                    [field]: e.target.value,
                    retailer_name: retailer.name
                  }
                }
                return { ...p, [field]: e.target.value }
              })
            }
          }
        }))
      }

      const val_product = (_id, field) => e => {
        const ok = validate(field, e.target.value)
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            farm_details: {
              ...s.canvass.farm_details,
              products: s.canvass.farm_details.products.map(p => {
                if (p._id != _id) return p
                return { ...p, [`${field}_ok`]: ok }
              })
            }
          }
        }))
      }

      const remove_product = _id => e => {
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            farm_details: {
              ...s.canvass.farm_details,
              products: s.canvass.farm_details.products.filter(p => p._id != _id)
            }
          }
        }))
      }

      const validate_email = email => {
        if (email === '') return true
        const re = /\S{2,}@\S{2,}\.\S{2,}/
        return re.test(email)
      }
      const validate_phone = phone => {
        if (phone === '') return true
        const re = /^(\+64|64|0)\d{8,10}$/
        return re.test(phone)
      }
      const validate_name = name => {
        const re = /^[a-zA-Z\s-]{2,}$/
        return re.test(name)
      }
      const validate_address = address => {
        const re = /^[a-zA-Z0-9,\s-]{2,}$/
        return re.test(address)
      }
      const validate_region = region => {
        const re = /^[a-zA-Z\s-]{2,}$/
        return re.test(region)
      }
      const validate_dairy_number = dairy_number => {
        const re = /^\d{1,}$/
        return re.test(dairy_number)
      }
      const validate_herd_size = herd_size => {
        if (herd_size === '') return true
        const re = /^\d{1,}$/
        return re.test(herd_size)
      }
      const validate_ttx_volume = ttx_volume_on_hand => {
        if (ttx_volume_on_hand === '') return true
        const re = /^\d{1,}$/
        return re.test(ttx_volume_on_hand)
      }
      const validate_days_remaining = days_remaining => {
        if (days_remaining === '') return true
        const re = /^\d{1,}$/
        return re.test(days_remaining)
      }
      const validate_quantity = quantity => {
        if (quantity === '') return false
        const re = /^\d{1,}$/
        return re.test(quantity)
      }
      const validate_agreed_price = agreed_price => {
        if (agreed_price === '') return true
        const re = /^\d{2,}(\.\d{2})?$/
        return re.test(agreed_price)
      }

      const validate_date = date => {
        const d = date.replace(/[^\d-]/g, '')
        if (d == '--') return true
        const re = /^\d{2}-\d{2}-\d{4}$/
        return re.test(d)
      }

      const validate = (field, value) => {
        if (field.includes('email_address')) return validate_email(value)
        if (field.includes('phone_number')) return validate_phone(value)
        if (field.includes('name')) return validate_name(value)
        if (field.includes('address')) return validate_address(value)
        if (field.includes('region')) return validate_region(value)
        if (field.includes('dairy_number')) return validate_dairy_number(value)
        if (field.includes('herd_size')) return validate_herd_size(value)
        if (field.includes('ttx_volume_on_hand')) return validate_ttx_volume(value)
        if (field.includes('days_remaining')) return validate_days_remaining(value)
        if (field.includes('quantity')) return validate_quantity(value)
        if (field.includes('agreed_price')) return validate_agreed_price(value)
        if (field.includes('date')) return validate_date(value)
      }

      const should_display_ok = v => {
        return v === undefined ? false : v ? false : true
      }

      const products = data?.products.filter(p => p.name && p.brand == 'Deosan' && p.price) ?? []

      const product_enhancements = name => {
        switch (name) {
          case 'TeatX Plus':
            return {
              ordering_priority: 1,
              group: 'Teat Spray'
            }
          case 'TeatX':
            return {
              ordering_priority: 2,
              group: 'Teat Spray'
            }
          case 'Triodex':
            return {
              ordering_priority: 3,
              group: 'Teat Spray'
            }
          case 'Zoom Acid':
            return {
              ordering_priority: 4,
              group: 'Acid Detergent'
            }
          case 'Alkali Powder':
            return {
              ordering_priority: 5,
              group: 'Alkali Detergent'
            }
          case 'Chlor Alkali':
            return {
              ordering_priority: 6,
              group: 'Alkali Detergent'
            }
          case 'Hypo Chlor':
            return {
              ordering_priority: 7,
              group: 'Alkali Detergent'
            }
          case 'Alkali Liquid':
            return {
              ordering_priority: 8,
              group: 'Alkali Detergent'
            }
          case 'Bloat Control':
            return {
              ordering_priority: 9,
              group: 'Bloat Control'
            }
          case 'Combi 4 Chelate':
            return {
              ordering_priority: 10,
              group: 'Mineral'
            }
          case 'Combi 5 Chelate':
            return {
              ordering_priority: 11,
              group: 'Mineral'
            }
          case 'Amino 18':
            return {
              ordering_priority: 12,
              group: 'Mineral'
            }
          case 'Copper Chelate':
            return {
              ordering_priority: 13,
              group: 'Mineral'
            }
          case 'Cobalt Chelate':
            return {
              ordering_priority: 14,
              group: 'Mineral'
            }
          case 'Iodine Chelate':
            return {
              ordering_priority: 15,
              group: 'Mineral'
            }
          case 'Zinc Chelate':
            return {
              ordering_priority: 16,
              group: 'Mineral'
            }
          case 'Liquid Selenium':
            return {
              ordering_priority: 17,
              group: 'Mineral'
            }
          case 'Virsus':
            return {
              ordering_priority: 18,
              group: 'Disinfectant'
            }
          default:
            return {
              ordering_priority: 19,
              group: 'Other'
            }
        }
      }

      const grouped_products = products
        .map(p => {
          const enhancements = product_enhancements(p.name)
          return {
            ...p,
            ...enhancements
          }
        })
        .sort((a, b) => {
          return a.ordering_priority - b.ordering_priority || Number(a.pack_size) - Number(b.pack_size)
        })
        .reduce((g, p) => {
          if (!g[p.group]) g[p.group] = []
          g[p.group].push(p)
          return g
        }, {})

      const render_select_group = group => {
        const items = grouped_products[group].map(p => {
          return (
            <MenuItem key={p.id} value={p.id}>
              {p.name} - {p.pack_size}
              {p.pack_size_unit}
            </MenuItem>
          )
        })
        return [<ListSubheader>{group}</ListSubheader>, ...items]
      }

      const use_price = (price, agreed_price) => {
        return Number(agreed_price && agreed_price !== price ? agreed_price : price)
      }

      const reason_no_business_options = ['Price', 'Service', 'Relationship', 'Other']

      const change_reason_no_business = e => {
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            reason_no_business: e.target.value
          }
        }))
      }

      const add_recipient = email_address => {
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            recipients: [...(s.canvass.recipients ?? []), email_address]
          }
        }))
      }

      const remove_recipient = email_address => {
        set_state(s => ({
          ...s,
          canvass: {
            ...s.canvass,
            recipients: s.canvass.recipients.filter(e => e != email_address)
          }
        }))
      }

      const toggle_recipient = email_address => e => {
        e.preventDefault()
        if (state.canvass.recipients.includes(email_address)) {
          remove_recipient(email_address)
        } else {
          add_recipient(email_address)
        }
      }

      useEffect(() => {
        const island_retailers =
          data?.retailers?.filter(r => r.island == state.canvass?.island).sort((a, b) => (a.name > b.name ? 1 : -1)) ??
          []
        const region_retailers =
          data?.retailers
            ?.filter(r => r.region == state.canvass?.farm_details.region)
            .sort((a, b) => (a.name > b.name ? 1 : -1)) ?? []
        set_state(s => ({ ...s, region_retailers, island_retailers }))
      }, [data?.retailers, state.canvass?.island, state.canvass?.farm_details.region])

      const should_disable_submit = () => {
        // console.log(JSON.stringify(state, null, 2))
        // console.log(1)
        if (!state.canvass.date) return true
        // console.log(2)
        if (!validate_dairy_number(state.canvass.dairy_number)) return true
        // console.log(3)
        if (!state.canvass.island) return true
        // console.log(4)
        if (!validate_region(state.canvass.region)) return true
        // console.log(5)
        if (!state.canvass.farm_details.address) return true
        // console.log(6)
        if (state.canvass.farm_details.products.some(p => !p.delivery_date || isNaN(p.quantity) || !p.quantity))
          return true
        // console.log(7)
        if (!state.canvass.farm_details.products.length) return true
        if (
          state.canvass.farm_details.contacts.some(
            p =>
              !p.role ||
              !validate_name(p.name) ||
              (p.email_address && !validate_email(p.email_address)) ||
              (p.phone_number && !validate_phone(p.phone_number))
          )
        )
          return true
        // console.log(8)
        if (state.canvass.ttx_volume_on_hand && !validate_ttx_volume(state.canvass.ttx_volume_on_hand)) return true
        // console.log(9)
        if (state.canvass.days_remaining && !validate_days_remaining(state.canvass.days_remaining)) return true
        // console.log(10)
        if (!state.canvass.agreement_status) return true
        // console.log(11)
        if (JSON.stringify(state, null, 2).includes(`_ok": false`)) return true
        // console.log(12)
        return false
      }

      const submit_canvass = async e => {
        e.preventDefault()
        const { queued_canvasses } = await IndexedDB.get_items(['queued_canvasses'])
        await IndexedDB.set_items({
          queued_canvasses: [
            ...(queued_canvasses ?? []),
            {
              id: state.canvass.id,
              date: DateTime.fromISO(state.canvass.date).toFormat('dd-MM-yyyy'),
              island: state.canvass.island,
              shed_id: state.canvass.shed_id,
              dairy_number: Number(state.canvass.dairy_number),
              note_farm: state.canvass.note_farm,
              calving_date: state.canvass.calving_date,
              ttx_volume_on_hand: Number(state.canvass.ttx_volume_on_hand),
              days_remaining: Number(state.canvass.days_remaining),
              agreement_status: state.canvass.agreement_status,
              reason_no_business: state.canvass.reason_no_business,
              note_no_business: state.canvass.note_no_business,
              farm_details: {
                region: state.canvass.farm_details.region,
                address: state.canvass.farm_details.address,
                herd_size: state.shed.herd_size,
                contacts: state.canvass.farm_details.contacts.map(c => ({
                  id: c.id,
                  contact_id: c.contact_id,
                  role: c.role,
                  name: c.name,
                  trading_name: c.trading_name,
                  email_address: c.email_address,
                  phone_number: c.phone_number,
                  retailer_id: c.retailer_id
                })),
                products: state.canvass.farm_details.products.map(p => ({
                  product_id: p.product_id,
                  name: p.name,
                  delivery_date: p.delivery_date,
                  quantity: Number(p.quantity),
                  price: Number(p.price),
                  agreed_price: use_price(p.price, p.agreed_price),
                  pack_size: p.pack_size,
                  pack_size_unit: p.pack_size_unit,
                  category: p.category,
                  retailer_id: p.retailer_id,
                  retailer_name: p.retailer_name
                }))
              },
              herd_size: state.shed.herd_size,
              recipients: state.canvass.recipients,
              updated_at: Date.now()
            }
          ]
        })
        hub.emit('test sync')
        hub.emit('sync')
        if (contact_id) return page(`/contacts/${contact_id}`)
        if (state.canvass.contact_id) return page(`/contacts/${state.canvass.contact_id}`)
        page('/contacts')
      }

      return (
        <>
          <Box
            sx={{
              backgroundColor: 'whitesmoke',
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}
          >
            <Breadcrumbs aria-label='breadcrumb' separator={<NavigateNext fontSize='small' />}>
              <Link underline='hover' color='inherit' href='/contacts'>
                Contacts
              </Link>
              {contact_id ? (
                <Link underline='hover' color='inherit' href={`/contacts/${contact_id}`}>
                  {state.contact?.name ? state.contact.name : contact_id}
                </Link>
              ) : null}
            </Breadcrumbs>
          </Box>
          <Fab
            variant='extended'
            color='primary'
            onClick={() => page(`/canvass/create/${contact_id}`)}
            sx={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              zIndex: 1000
            }}
          >
            <ContactPage sx={{ mr: 1 }} />
            New Canvass
          </Fab>
          {state.contact ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                // padding: '1rem',
                marginBottom: '10rem',
                gap: '1rem'
              }}
            >
              {state.canvass ? (
                <FormControl sx={{ gap: '1rem', margin: '2rem 1rem 10rem 1rem' }}>
                  <h2 style={{ marginBottom: 0 }}>Date</h2>
                  <span>{DateTime.fromISO(state.canvass.date).toFormat('dd MMMM yyyy')}</span>
                  <h2 style={{ marginBottom: 0 }}>Territory Manager</h2>
                  <span>{[state.shed?.tm?.first_name, state.shed?.tm?.last_name].join(' ')}</span>
                  <h2 style={{ marginBottom: 0 }}>Shed</h2>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '1rem'
                    }}
                  >
                    <span>{state.shed?.address}</span>
                    <span>{{ north: 'North Island', south: 'South Island' }[state.shed?.island]}</span>
                    <span>{state.shed?.region}</span>
                  </Box>
                  {state.canvass.note_farm ? (
                    <>
                      <h2 style={{ marginBottom: 0 }}>Farm Note</h2>
                      <span>{state.canvass.note_farm}</span>
                    </>
                  ) : null}
                  {state.canvass.agreement_status !== 'No Business' ? (
                    <h2 style={{ marginBottom: 0 }}>Products</h2>
                  ) : null}
                  {state.canvass.farm_details.products.length > 0
                    ? state.canvass.farm_details.products.map((p, i) => {
                        const product = products.find(pr => pr.id === p.product_id)
                        return (
                          <FormGroup key={p._id} sx={{ gap: '1rem', marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0 }}>{`${product.name} ${product.pack_size}L`}</h3>
                            {/* <FormGroup row>
                              <Button variant='contained' onClick={remove_product(p._id)}>
                                Remove
                              </Button>
                            </FormGroup> */}
                            <FormGroup row sx={{ gap: '1rem' }}>
                              <TextField
                                label='Product Quantity'
                                defaultValue={1}
                                onChange={update_product(p._id, 'quantity')}
                                onBlur={val_product(p._id, 'quantity')}
                                error={should_display_ok(p.quantity_ok)}
                                helperText={should_display_ok(p.quantity_ok) && 'Invalid product quantity.'}
                                disabled={true}
                              />
                              <TextField
                                label='Price'
                                value={p.price}
                                onChange={update_product(p._id, 'price')}
                                InputProps={{
                                  startAdornment: <InputAdornment position='start'>$</InputAdornment>
                                }}
                                disabled={true}
                                sx={{ maxWidth: 200 }}
                              />
                              <TextField
                                label='Agreed Price'
                                value={p.agreed_price ?? ''}
                                onChange={update_product(p._id, 'agreed_price')}
                                onBlur={val_product(p._id, 'agreed_price')}
                                error={should_display_ok(p.agreed_price_ok)}
                                helperText={should_display_ok(p.agreed_price_ok) && 'Invalid agreed price.'}
                                InputProps={{
                                  startAdornment: <InputAdornment position='start'>$</InputAdornment>
                                }}
                                disabled={true}
                                sx={{ maxWidth: 200 }}
                              />
                              <LocalizationProvider dateAdapter={AdapterLuxon}>
                                <DatePicker
                                  label='Delivery Date'
                                  format='dd-MM-yyyy'
                                  onChange={update_product(p._id, 'delivery_date')}
                                  value={DateTime.fromFormat(p.delivery_date ?? '', 'dd-MM-yyyy')}
                                  slotProps={{
                                    textField: {
                                      onBlur: val_product(p._id, 'delivery_date'),
                                      error: should_display_ok(p.delivery_date_ok),
                                      helperText: should_display_ok(p.delivery_date_ok) && 'Invalid delivery date.'
                                    }
                                  }}
                                  disabled={true}
                                  sx={{ maxWidth: 200 }}
                                />
                              </LocalizationProvider>
                              <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel id='region-select-label'>Retailer</InputLabel>
                                <Select
                                  labelId='region-select-label'
                                  id='region-select'
                                  value={p.retailer_id ? p.retailer_id : ''}
                                  onChange={update_product(p._id, 'retailer_id')}
                                  disabled={true}
                                >
                                  {state.region_retailers.length ? <ListSubheader>By Region</ListSubheader> : null}
                                  {state.region_retailers.map(r => (
                                    <MenuItem key={r.id} value={r.id}>
                                      {r.name}
                                    </MenuItem>
                                  ))}
                                  {state.island_retailers.length ? <ListSubheader>By Island</ListSubheader> : null}
                                  {state.island_retailers.map(r => (
                                    <MenuItem key={r.id} value={r.id}>
                                      {r.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </FormGroup>
                          </FormGroup>
                        )
                      })
                    : null}
                  {/* {state.canvass.agreement_status !== 'No Business' ? (
                    <FormGroup row>
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel id='products-label' htmlFor='products'>
                          Product List
                        </InputLabel>
                        <Select labelId='products-label' id='products' value={''} onChange={add_product}>
                          {Object.keys(grouped_products).map(pk => render_select_group(pk))}
                        </Select>
                      </FormControl>
                    </FormGroup>
                  ) : null} */}
                  {/* <h2 style={{ marginBottom: 0 }}>Aggreement Status</h2>
                  <FormGroup>
                    <RadioGroup
                      aria-labelledby='agreement-status-radio-group-label'
                      name='agreement-status-radio-group'
                      defaultValue={state.canvass.agreement_status}
                      value={state.canvass.agreement_status}
                      onChange={change_agreement_status}
                      sx={{
                        flexDirection: 'row'
                      }}
                    >
                      {agreement_status_options.map(option => {
                        const is_disabled = {
                          Pending: state.canvass.farm_details.products.length === 0,
                          Confirmed: state.canvass.farm_details.products.length === 0,
                          'No Business': state.canvass.farm_details.products.length > 0
                        }[option]
                        return (
                          <FormControlLabel
                            key={option}
                            value={option}
                            control={<Radio />}
                            label={option}
                            disabled={is_disabled}
                          />
                        )
                      })}
                    </RadioGroup>
                  </FormGroup> */}
                  {/* {state.canvass.agreement_status === 'No Business' ? (
                    <>
                      <h2 style={{ marginBottom: 0 }}>Reason for No Business</h2>
                      <span>{state.canvass.reason_no_business}</span>
                      <div>{state.canvass.note_no_business}</div>
                    </>
                  ) : null} */}
                  {/* {state.canvass.available_recipients?.length > 0 ? (
                    <>
                      <h2 style={{ margin: '1rem 0 0 0' }}>Resend Agreement to Supply to Recipients</h2>
                      <FormGroup row sx={{ gap: '1rem' }}>
                        {state.canvass.available_recipients.map(email_address => {
                          const is_added = state.canvass.recipients.includes(email_address)
                          return (
                            <FormControlLabel
                              key={email_address}
                              control={<Checkbox checked={is_added} />}
                              label={email_address}
                              onClick={toggle_recipient(email_address)}
                            />
                          )
                        })}
                      </FormGroup>
                    </>
                  ) : null} */}
                  {/* {state.canvass.agreement_status !== 'No Business' ? (
                    <FormGroup row sx={{ marginTop: '2rem' }}>
                      <Button variant='contained' onClick={submit_canvass} disabled={should_disable_submit()}>
                        Update Canvass
                      </Button>
                    </FormGroup>
                  ) : null} */}
                </FormControl>
              ) : null}
            </Box>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: '0px',
                left: '0px',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: -1
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </>
      )
    }
  ])
})
